import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import axios from 'axios';
import EventPopup from './EventPopup';
import AppIcon from '../common/AppIcon';
import './Calendar.css';
import { getApiBaseUrl } from '../../services/api';
import { emitDataChange, onDataChange, DATA_TYPES, shouldRefresh } from '../../services/dataEvents';

const Calendar = forwardRef((props, ref) => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [referenceElement, setReferenceElement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [hospitals, setHospitals] = useState([]);
  const [showHospitalSlots, setShowHospitalSlots] = useState(true);
  const [calendarView, setCalendarView] = useState(localStorage.getItem('ui_calendar_view') || 'month');
  const [showWeekends, setShowWeekends] = useState((localStorage.getItem('ui_show_weekends') || 'true') === 'true');

  const apiUrl = getApiBaseUrl();

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch both events and hospitals
      const [eventsResponse, hospitalsResponse] = await Promise.all([
        axios.get(`${apiUrl}/api/processedresult`),
        axios.get(`${apiUrl}/api/hospitals`)
      ]);
      
      // Set hospitals data
      const hospitalsData = Array.isArray(hospitalsResponse.data) ? hospitalsResponse.data : [];
      setHospitals(hospitalsData);
      
      // Use the correct endpoint for the Node.js backend
      const response = eventsResponse;
      
      // The API returns an array of schedule data
      let allEvents = [];
      const bookedByHospitalDay = {}; // { [code]: { [day]: count } }
      
      if (response.data && Array.isArray(response.data)) {
        response.data.forEach(event => {
          if (event && event.name && event.shift_year && event.shift_month && event.shift_day) {
            const code = event.location;
            const day = event.shift_day;
            if (code && day) {
              bookedByHospitalDay[code] = bookedByHospitalDay[code] || {};
              bookedByHospitalDay[code][day] = (bookedByHospitalDay[code][day] || 0) + 1;
            }
            allEvents.push({
              id: event.uid, // Use the actual uid from backend
              title: `${event.name} (${event.location})`,
              start: new Date(event.shift_year, event.shift_month - 1, event.shift_day),
              // FullCalendar expects `classNames` (array or string), not `className`
              classNames: [event.conflict ? 'conflict' : 'no-conflict'],
              extendedProps: {
                location: event.location,
                conflict_with: event.conflict_with || [],
                conflict_at: event.conflict_at || [],
                shift_time: event.shift_time || '24 Hours',
                doctor_name: event.name,
                uid: event.uid // Store uid for backend operations
              }
            });
          }
        });
      }
      
      // Add hospital slot events if enabled
      if (showHospitalSlots && hospitalsData.length > 0) {
        const hospitalSlotEvents = generateHospitalSlotEvents(hospitalsData, bookedByHospitalDay);
        allEvents = [...allEvents, ...hospitalSlotEvents];
      }
      
      setEvents(allEvents);
      console.log('Fetched events:', allEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
      setError('Failed to load schedule data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, showHospitalSlots]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    return onDataChange((type) => {
      if (shouldRefresh(type, [DATA_TYPES.SHIFTS, DATA_TYPES.HOSPITALS, DATA_TYPES.GROUP, DATA_TYPES.ALL])) {
        fetchEvents();
      }
    });
  }, [fetchEvents]);

  useEffect(() => {
    const handler = () => {
      setCalendarView(localStorage.getItem('ui_calendar_view') || 'month');
      setShowWeekends((localStorage.getItem('ui_show_weekends') || 'true') === 'true');
    };
    window.addEventListener('uiSettingsChanged', handler);
    return () => window.removeEventListener('uiSettingsChanged', handler);
  }, []);

  // Generate hospital slot events for the current month
  const generateHospitalSlotEvents = (hospitalsData, bookedByHospitalDay = {}) => {
    const slotEvents = [];
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    
    hospitalsData.forEach(hospital => {
      const monthlySlots = hospital.monthlySlots || 60;
      const slotDuration = hospital.slotDuration || 24;
      const slotsPerDay = Math.ceil(monthlySlots / 30); // Distribute slots across month
      
      // Generate slot events for each day of the month
      for (let day = 1; day <= 31; day++) {
        const date = new Date(currentYear, currentMonth, day);
        if (date.getMonth() === currentMonth) { // Only for current month
          const booked = (bookedByHospitalDay?.[hospital.code]?.[day] || 0);
          const available = Math.max(0, slotsPerDay - booked);
          slotEvents.push({
            id: `slot-${hospital.code}-${day}`,
            title: `${hospital.code} - ${available} slots available`,
            start: date,
            allDay: true,
            classNames: ['hospital-slot'],
            backgroundColor: '#e3f2fd',
            borderColor: '#2196f3',
            textColor: '#1976d2',
            extendedProps: {
              type: 'hospital-slot',
              hospitalCode: hospital.code,
              hospitalName: hospital.name,
              availableSlots: available,
              slotDuration: slotDuration,
              monthlySlots: monthlySlots
            }
          });
        }
      }
    });
    
    return slotEvents;
  };

  useImperativeHandle(ref, () => ({
    refreshEvents: fetchEvents,
  }));

  const handleEventClick = (eventInfo) => {
    setSelectedEvent(eventInfo.event);
    setReferenceElement(eventInfo.el);
  };

  const closePopup = () => {
    setSelectedEvent(null);
    setReferenceElement(null);
  };

  const handleEventDrop = async (dropInfo) => {
    const event = dropInfo.event;
    const newDate = event.start;
    const eventId = event.extendedProps.uid || event.id; // Use uid from extendedProps
    
    try {
      // Format date as dd-mm-yyyy for backend
      const formattedDate = `${newDate.getDate().toString().padStart(2, '0')}-${(newDate.getMonth() + 1).toString().padStart(2, '0')}-${newDate.getFullYear()}`;
      
      // Update the shift date on the backend
      const response = await axios.put(`${apiUrl}/api/shifts/${eventId}`, {
        shift_date: formattedDate
      });
      
      if (response.status === 200) {
        await fetchEvents();
        emitDataChange(DATA_TYPES.SHIFTS);
        console.log('Event dropped successfully:', event.title, 'to', formattedDate);
      } else {
        throw new Error('Failed to update shift');
      }
    } catch (error) {
      console.error('Error updating shift:', error);
      // Revert the drop if update failed
      dropInfo.revert();
      setError('Failed to move shift. Please try again.');
    }
  };

  const handleEventResize = async (resizeInfo) => {
    // Handle event duration changes if needed
    console.log('Event resized:', resizeInfo);
  };

  const deleteEvent = async (eventId) => {
    try {
      const response = await axios.delete(`${apiUrl}/api/shifts/${eventId}`);
      if (response.status === 200) {
        await fetchEvents();
        emitDataChange(DATA_TYPES.SHIFTS);
        closePopup();
        console.log('Event deleted successfully:', eventId);
      } else {
        throw new Error('Failed to delete shift');
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      setError('Failed to delete shift. Please try again.');
    }
  };

  const renderEventContent = (eventInfo) => {
    return (
      <div className="fc-event-custom">
        <div className="fc-event-title">{eventInfo.event.title}</div>
        <div className="fc-event-actions">
          <button 
            className="fc-event-edit-btn"
            onClick={(e) => {
              e.stopPropagation();
              setEditingEvent(eventInfo.event);
            }}
          >
            ✏️
          </button>
        </div>
      </div>
    );
  };

  const handleRefresh = () => {
    setError(null);
    fetchEvents();
  };

  const handleToday = () => {
    if (props.onToday) {
      props.onToday();
    }
  };

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (loading) {
    return (
      <div className="calendar-container">
        <div className="calendar-loading">
          <div className="calendar-loading-spinner"></div>
          <span>Loading schedule...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="calendar-container">
        <div className="calendar-loading">
          <span style={{ color: 'var(--error-color)' }}>⚠️ {error}</span>
          <button 
            className="calendar-action-btn" 
            onClick={handleRefresh}
            style={{ marginTop: 'var(--spacing-md)' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <h2 className="calendar-title">
          <AppIcon name="calendar" size={26} strokeWidth={2.25} className="app-icon--primary" />
          Schedule Overview
        </h2>
        <div className="calendar-actions">
          <button type="button" className="calendar-action-btn" onClick={handleRefresh}>
            <AppIcon name="refresh" size={18} strokeWidth={2.25} />
            Refresh
          </button>
          <button type="button" className="calendar-action-btn" onClick={handleToday}>
            <AppIcon name="today" size={18} strokeWidth={2.25} />
            Today
          </button>
          <button 
            type="button"
            className={`calendar-action-btn ${showHospitalSlots ? 'active' : ''}`}
            onClick={() => {
              setShowHospitalSlots(!showHospitalSlots);
            }}
          >
            <AppIcon name={showHospitalSlots ? 'hideSlots' : 'showSlots'} size={18} strokeWidth={2.25} />
            {showHospitalSlots ? 'Hide Slots' : 'Show Slots'}
          </button>
        </div>
      </div>

      <FullCalendar
        key={`${calendarView}-${showWeekends ? 'wknd' : 'nowknd'}`}
        plugins={[dayGridPlugin, interactionPlugin]}
        initialView={calendarView === 'week' ? 'dayGridWeek' : 'dayGridMonth'}
        events={events}
        eventClick={handleEventClick}
        eventDrop={handleEventDrop}
        eventResize={handleEventResize}
        eventContent={renderEventContent}
        editable={true}
        droppable={true}
        headerToolbar={{
          left: 'prev,next',
          center: 'title',
          right: 'dayGridMonth,dayGridWeek'
        }}
        height="auto"
        dayMaxEvents={3}
        moreLinkClick="popover"
        eventDisplay="block"
        selectable={true}
        selectMirror={true}
        weekends={showWeekends}
        firstDay={1}
        weekNumbers={false}
        weekNumberCalculation="ISO"
        eventDurationEditable={false}
        eventStartEditable={true}
        eventResizableFromStart={false}
        // Allow multiple shifts on same day, and allow moving freely.
        eventOverlap={true}
        unselectAuto={true}
        eventTimeFormat={{
          hour: 'numeric',
          minute: '2-digit',
          meridiem: 'short'
        }}
        titleFormat={{
          month: 'long',
          year: 'numeric'
        }}
        dayHeaderFormat={{
          weekday: 'short'
        }}
        buttonText={{
          today: 'Today',
          month: 'Month',
          week: 'Week'
        }}
        eventConstraint={{
          start: '00:00',
          end: '24:00',
          dows: [0, 1, 2, 3, 4, 5, 6]
        }}
        eventAllow={(dropInfo, draggedEvent) => {
          // Prevent dragging the blue "slot overlay" blocks.
          if (draggedEvent?.extendedProps?.type === 'hospital-slot') return false;
          return true;
        }}
      />

      {selectedEvent && (
        <EventPopup
          event={selectedEvent}
          referenceElement={referenceElement}
          onClose={closePopup}
          onDelete={deleteEvent}
        />
      )}

      {editingEvent && (
        <div className="edit-modal">
          <div className="edit-modal-content">
            <h3>Edit Shift</h3>
            <p>Drag and drop the shift to move it to a different date.</p>
            <button onClick={() => setEditingEvent(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
});

export default Calendar;